import type { TOnboardingAnswerRequirements } from "@/api/onboarding";

/**
 * Get a user-friendly label for the answer type.
 */
export function getAnswerTypeLabel(dataType: string): string {
  const labels: Record<string, string> = {
    text: "Text Response",
    number: "Number",
    multiple_choice: "Select Option",
    file_upload: "File Upload",
    text_or_file: "Text or File",
    text_with_file: "Text (file optional)",
    file_with_text: "File (text optional)",
  };
  return labels[dataType] || dataType;
}

/**
 * Get guidance text based on the data type.
 */
export function getAnswerTypeGuidance(dataType: string): string {
  const guidance: Record<string, string> = {
    text: "Enter a text response",
    number: "Enter a numeric value",
    multiple_choice: "Choose from the available options",
    file_upload: "Upload a file",
    text_or_file: "Provide text OR upload a file",
    text_with_file: "Text required, file is optional",
    file_with_text: "File required, text is optional",
  };
  return guidance[dataType] || "";
}

/**
 * Get detailed guidance based on answer requirements.
 */
export function getAnswerGuidanceFromRequirements(
  requirements: TOnboardingAnswerRequirements | undefined,
  dataType: string
): string {
  if (!requirements) {
    return getAnswerTypeGuidance(dataType);
  }

  const parts: string[] = [];

  // Required inputs
  if (requirements.require_text && requirements.require_file) {
    parts.push("Both text and file are required");
  } else if (requirements.require_text) {
    parts.push("Text response required");
  } else if (requirements.require_file) {
    parts.push("File upload required");
  } else if (requirements.require_either) {
    parts.push("Provide text OR upload a file");
  }

  // Accepted inputs (if not already required)
  if (requirements.accept_file && !requirements.require_file && !requirements.require_either) {
    parts.push("You can also attach a file");
  }

  // Text constraints
  if (requirements.text_min_length) {
    parts.push(`Minimum ${requirements.text_min_length} characters`);
  }
  if (requirements.text_max_length) {
    parts.push(`Maximum ${requirements.text_max_length} characters`);
  }

  // Number constraints
  if (requirements.must_be_number) {
    const numParts: string[] = [];
    if (requirements.min_value !== null && requirements.min_value !== undefined) {
      numParts.push(`min: ${requirements.min_value}`);
    }
    if (requirements.max_value !== null && requirements.max_value !== undefined) {
      numParts.push(`max: ${requirements.max_value}`);
    }
    if (numParts.length > 0) {
      parts.push(`Number range: ${numParts.join(", ")}`);
    }
  }

  // File constraints
  if (requirements.accept_file || requirements.require_file) {
    if (requirements.file_types && requirements.file_types.length > 0) {
      parts.push(`Accepted files: ${requirements.file_types.join(", ")}`);
    }
    if (requirements.max_file_size_mb) {
      parts.push(`Max file size: ${requirements.max_file_size_mb}MB`);
    }
  }

  return parts.length > 0 ? parts.join(" • ") : getAnswerTypeGuidance(dataType);
}

/**
 * Get icon name based on answer type for visual indication.
 */
export function getAnswerTypeIcon(dataType: string): string {
  const icons: Record<string, string> = {
    text: "FileText",
    number: "Hash",
    multiple_choice: "List",
    file_upload: "Upload",
    text_or_file: "FileText",
    text_with_file: "FileText",
    file_with_text: "Upload",
  };
  return icons[dataType] || "FileText";
}
